import { useState } from "react";
import { useQuery, type UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import { DynamicForm } from "../dynamicForm";
import { userToAutocompleteOption } from "../shared/Autocomplete";
import { PersonIcon3 } from "../shared/icons";
import {
  enterpriseCustomerUserSelectOptionsQueryOptions,
  type EnterpriseCustomerOrganisation,
  type UpdateProductAdminsPayload,
} from "../api/enterpriseCustomer";
import { AddProductAdminDialog } from "./AddProductAdminDialog";

export const ProductAdminsField = ({
  organisation,
  updateProductAdminsMutation,
}: {
  organisation: EnterpriseCustomerOrganisation;
  updateProductAdminsMutation: UseMutationResult<unknown, Error, UpdateProductAdminsPayload>;
}) => {
  const { t } = useTranslation();
  const customerUsersQuery = useQuery(enterpriseCustomerUserSelectOptionsQueryOptions());
  const [newAdminEmail, setNewAdminEmail] = useState<string | null>(null);

  return (
    <>
      <DynamicForm.InlineFieldPrimary
        htmlFor="product-admins"
        label={t("EnterpriseCustomer.Organisation.ProductAdmins.Label")}
        infoPopoverContent={{ body: t("Roles.PRODUCT_ADMIN.Info") }}
        input={
          <DynamicForm.InlineEntityMultiSelect
            id="product-admins"
            value={organisation.productAdmins.map(userToAutocompleteOption)}
            options={customerUsersQuery.data?.map(userToAutocompleteOption)}
            onSave={selected =>
              updateProductAdminsMutation.mutateAsync({
                organisationId: organisation.id,
                userIds: selected.map(({ value }) => value),
              })
            }
            renderIcon={iconProps => <PersonIcon3 {...iconProps} />}
            noOptionsTextKey="DynamicForm.PersonSelect.Empty"
            onCreateNew={setNewAdminEmail}
          />
        }
      />
      {newAdminEmail !== null && customerUsersQuery.data && (
        <AddProductAdminDialog
          organisation={organisation}
          customerUsers={customerUsersQuery.data}
          updateProductAdminsMutation={updateProductAdminsMutation}
          defaultEmail={newAdminEmail}
          onClose={() => setNewAdminEmail(null)}
        />
      )}
    </>
  );
};
